<template>
  <el-container class="paper-assembly-container">
    <el-main>
      <el-page-header content="组卷控制台" @back="handleBack" />
      <el-divider />

      <el-form ref="paperForm" :model="paperForm" :rules="rules" label-width="100px" size="medium" class="optimized-paper-form">
        <!-- 第一行：试卷主名称（通栏，突出核心信息） -->
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="试卷名称" prop="title">
              <el-input v-model="paperForm.title" placeholder="如：2026年高考模拟测评理科综合能力测试" clearable />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 第二行：核心元数据 + 总分看板（通过 flex 布局实现两端对齐） -->
        <el-row :gutter="20" type="flex" justify="space-between" align="top">
          <!-- 左侧：三个并排的参数输入框 -->
          <el-col :span="18">
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item label="测评科目" prop="subjectId">
                  <el-select v-model="paperForm.subjectId" placeholder="请选择科目" style="width:100%">
                    <el-option
                      v-for="item in allSubject"
                      :key="item.label"
                      :label="item.label"
                      :value="item.value"
                    />
                  </el-select>
                </el-form-item>
              </el-col>

              <!-- 修正了原代码中 duration 的 prop 误写为 title 的 Bug -->
              <el-col :span="8">
                <el-form-item label="测评时长" prop="duration">
                  <el-input v-model="paperForm.duration" placeholder="输入分钟" type="number">
                    <template slot="append">分钟</template>
                  </el-input>
                </el-form-item>
              </el-col>

              <!-- 修正了原代码中 startTime 的 prop 误写为 title 的 Bug -->
              <el-col :span="8">
                <el-form-item label="测评时间" prop="startTime">
                  <el-date-picker
                    v-model="paperForm.startTime"
                    type="datetime"
                    placeholder="开始时间"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </el-col>

          <!-- 右侧：独立的得分荣誉看板，与左侧等高对齐 -->
          <el-col :span="6" class="score-summary-zone">
            <div class="total-score-badge">
              <div class="badge-label">试卷总分</div>
              <div class="badge-value"><span>{{ totalScore }}</span>分</div>
            </div>
          </el-col>
        </el-row>
      </el-form>

      <div class="sections-wrapper">
        <div style="margin-bottom: 15px; display: flex; justify-content: space-between; align-items: center;">
          <h3 class="section-main-title"><i class="el-icon-s-order" /> 卷面结构与大题配置</h3>
          <el-button type="primary" icon="el-icon-plus" size="small" @click="addPaperSection">添加新大题分块</el-button>
        </div>

        <el-empty v-if="paperForm.sections.length === 0" description="暂无大题配置，请点击右上方按钮添加大题（如：一、选择题）" />

        <div
          v-for="(sec, secIndex) in paperForm.sections"
          :key="sec.localId"
          class="section-card"
        >
          <div class="section-card-header">
            <el-row :gutter="10" type="flex" align="middle" style="width: 100%">
              <el-col :span="2" class="text-center">
                <span class="sec-num-text">第 {{ secIndex + 1 }} 部分</span>
              </el-col>
              <el-col :span="6">
                <el-input v-model="sec.sectionName" size="small" placeholder="大题名称，如：单项选择题" />
              </el-col>
              <el-col :span="8">
                <el-input v-model="sec.sectionHint" size="small" placeholder="大题说明（选填，如：每小题4分，共40分）" />
              </el-col>
              <el-col :span="4" class="text-right">
                <span class="sec-score-text">大题小计: <strong>{{ calculateSectionScore(sec) }}</strong> 分</span>
              </el-col>
              <el-col :span="4" class="text-right">
                <el-button type="text" icon="el-icon-plus" @click="openQuestionSelector(secIndex)">导入题目</el-button>
                <el-button type="text" style="color: #F56C6C" icon="el-icon-delete" @click="removeSection(secIndex)">删除大题</el-button>
              </el-col>
            </el-row>
          </div>

          <div class="section-card-body">
            <el-empty v-if="sec.questions.length === 0" :image-size="60" description="该大题下暂未放入试题，请点击右上角‘导入题目’" />

            <table v-else class="question-assemble-table">
              <thead>
                <tr>
                  <th width="70">题号</th>
                  <th width="100">题型</th>
                  <th>题目简述 / 题干预览</th>
                  <th width="120">分值设定</th>
                  <th width="120">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(q, qIndex) in sec.questions" :key="q.questionId">
                  <td class="text-center font-bold">{{ qIndex + 1 }}</td>
                  <td class="text-center">
                    <el-tag size="mini" :type="q.typeCode === 5 ? 'warning' : 'success'">{{ q.typeStr }}</el-tag>
                  </td>
                  <td>
                    <div class="table-q-content">{{ q.content }}</div>

                    <div v-if="q.typeCode === 5" class="table-sub-children-box">
                      <div v-for="(sub, subIdx) in q.children" :key="sub.questionId" class="sub-child-row">
                        <span>子问题 ({{ subIdx + 1 }}): 题干 [{{ sub.content }}]</span>
                        <div class="sub-score-input">
                          分值: <el-input-number v-model="sub.score" size="mini" :min="0" style="width:90px;" @change="syncCompositeTotalScore(q)" />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <el-input-number
                      v-model="q.score"
                      :min="0"
                      size="small"
                      style="width: 100%"
                      :disabled="q.typeCode === 5"
                    />
                    <small v-if="q.typeCode === 5" class="help-text">由子问题分值累加</small>
                  </td>
                  <td class="text-center">
                    <el-button type="text" icon="el-icon-top" :disabled="qIndex === 0" @click="moveQuestion(secIndex, qIndex, -1)" />
                    <el-button type="text" icon="el-icon-bottom" :disabled="qIndex === sec.questions.length - 1" @click="moveQuestion(secIndex, qIndex, 1)" />
                    <el-button type="text" style="color: #F56C6C" icon="el-icon-remove-outline" @click="removeQuestion(secIndex, qIndex)" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="paper-footer-bar">
        <el-button type="success" size="large" icon="el-icon-finished" @click="submitPaper">发布试卷并生成产物</el-button>
        <el-button size="large" @click="$emit('back')">取消组卷</el-button>
      </div>
    </el-main>

    <el-dialog title="选择题目加入大题" :visible.sync="selectorVisible" width="900px" append-to-body>
      <div style="margin-bottom: 15px;">
        <el-select v-model="queryParams.type" clearable placeholder="请选择试题类型" @change="onQuestionTypeChange">
          <el-option
            v-for="item in allType"
            :key="item.label"
            :label="item.label"
            :value="item.value"
          />
        </el-select>

        <el-input v-model="searchKey" placeholder="输入关键题干检索库内题目..." size="small" style="width: 300px; margin-right: 10px;" />
        <el-button type="primary" size="small" icon="el-icon-search">查询</el-button>
      </div>

      <el-table
        v-loading="loading"
        :data="dataList"
        border
        size="small"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="50" align="center" />
        <el-table-column property="questionId" label="题目ID" width="120" />
        <el-table-column property="type" label="题型" width="90" align="center" />
        <el-table-column property="content" label="题干简述" show-overflow-tooltip />
        <el-table-column property="level" label="难度" width="80" align="center" />
      </el-table>
      <el-pagination
        style="margin-top: 25px"
        layout="total, sizes, prev, pager, next, jumper"
        :page-sizes="[10, 20, 50]"
        :page-size="pageSize"
        :current-page="currentPage"
        :total="totalSize"
        @current-change="handleCurrentChange"
        @prev-click="handleCurrentChange"
        @next-click="handleCurrentChange"
      />

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="selectorVisible = false">取 消</el-button>
        <el-button type="primary" size="small" @click="confirmInsertQuestions">确认导入 ({{ selectedRows.length }}题)</el-button>
      </div>
    </el-dialog>
  </el-container>
</template>

<script>
import { addPaper, getQuestions, getQuestionType, getSubjectKV } from '@/api/exam'

export default {
  name: 'ExamPaperAssembly',
  data() {
    return {
      allSubject: [],
      allType: [],
      queryParams: {
        pageNumber: 1,
        subjectId: '',
        type: '1'
      },
      currentPage: 1,
      pageSize: 10,
      totalSize: 0,
      dataList: [],
      loading: true,
      paperForm: {
        title: '',
        subjectId: '',
        duration: 0,
        startTime: null,
        totalScore: 0,
        sections: [] // 试卷大题结构
      },
      rules: {
        title: [{ required: true, message: '请填写试卷名称', trigger: 'blur' }],
        subjectId: [{ required: true, message: '请选择测评科目', trigger: 'change' }]
      },
      // 题目选择器相关状态
      selectorVisible: false,
      currentSectionIndex: null, // 当前正为哪个大题导题
      searchKey: '',
      selectedRows: [] // 弹窗中勾选的题目
    }
  },
  computed: {
    // 实时计算整张试卷的绝对总分数
    totalScore() {
      let sum = 0
      this.paperForm.sections.forEach(sec => {
        sum += this.calculateSectionScore(sec)
      })
      return sum
    }
  },
  created() {
    document.title = '组卷控制台'
    this.getData()
  },
  methods: {
    getData() {
      getQuestionType().then((resp) => {
        if (resp.code === 0) {
          this.allType = resp.data
        } else {
          this.$message.warning(resp.msg)
        }
      })

      getSubjectKV().then((resp) => {
        if (resp.code === 0) {
          this.allSubject = resp.data
        }
      })
    },
    // 添加一个新的大题块
    addPaperSection() {
      this.paperForm.sections.push({
        localId: 'sec_' + Date.now() + Math.random(),
        sectionName: '',
        sectionHint: '',
        questions: []
      })
    },
    removeSection(index) {
      this.paperForm.sections.splice(index, 1)
    },
    // 计算单个大题块下所有题目的总分
    calculateSectionScore(section) {
      return section.questions.reduce((total, q) => total + (q.score || 0), 0)
    },
    // 针对复合题：当修改内部任何一个子问题的分数时，自动重新累加更新父组合题的 score
    syncCompositeTotalScore(question) {
      if (question.typeCode === 5 && question.children) {
        question.score = question.children.reduce((sum, sub) => sum + (sub.score || 0), 0)
      }
    },
    // 打开导题弹窗
    openQuestionSelector(secIndex) {
      this.getSubjectQuestions()
      this.currentSectionIndex = secIndex
      this.selectedRows = []
      this.selectorVisible = true
    },
    getSubjectQuestions() {
      this.dataList = []
      this.loading = true
      this.queryParams.subjectId = this.paperForm.subjectId
      getQuestions(this.queryParams).then(resp => {
        if (resp.code === 0) {
          this.dataList = resp.data.list
          this.totalSize = resp.data.totalSize
          this.loading = false
        } else {
          this.$message.warning(resp.msg)
        }
      }).catch(error => {
        this.$message.error(error.message)
      })
    },
    onQuestionTypeChange() {
      this.getSubjectQuestions()
    },
    handleSelectionChange(val) {
      this.selectedRows = val
    },
    handleCurrentChange(val) {
      this.queryParams.pageNumber = val
      this.getSubjectQuestions()
    },
    // 确认将勾选的题目插入到当前大题中
    confirmInsertQuestions() {
      if (this.selectedRows.length === 0) {
        this.selectorVisible = false
        return
      }
      const targetSec = this.paperForm.sections[this.currentSectionIndex]

      // 1. 收集整张试卷里已经存在的所有 questionId（防止跨大题或同大题重复添加）
      // 如果只想“当前大题内去重”，可以改为：const existingIds = targetSec.questions.map(q => q.questionId)
      const existingIds = []
      this.paperForm.sections.forEach(sec => {
        sec.questions.forEach(q => {
          existingIds.push(q.questionId)
        })
      })

      let skipCount = 0 // 记录被忽略的重复题目数量

      // 2. 深拷贝题目数据接入卷面，防止互串
      this.selectedRows.forEach(row => {
        // 判断是否已经存在
        if (existingIds.includes(row.questionId)) {
          skipCount++
          return // 已存在，直接忽略
        }

        const copyItem = JSON.parse(JSON.stringify(row))
        // 接入时如果是组合题，预先跑一次子分数同步
        this.syncCompositeTotalScore(copyItem)
        targetSec.questions.push(copyItem)
      })

      // 3. 友情提示反馈
      if (skipCount > 0) {
        this.$message.info(`成功导入 ${this.selectedRows.length - skipCount} 道题，忽略了 ${skipCount} 道已存在题目。`)
      } else {
        this.$message.success(`成功导入 ${this.selectedRows.length} 道题！`)
      }

      this.selectorVisible = false
    },
    removeQuestion(secIndex, qIndex) {
      this.paperForm.sections[secIndex].questions.splice(qIndex, 1)
    },
    // 调整题目在当前大题内的先后顺序
    moveQuestion(secIndex, qIndex, direction) {
      const questions = this.paperForm.sections[secIndex].questions
      const targetIndex = qIndex + direction
      if (targetIndex < 0 || targetIndex >= questions.length) return

      // 交换位置
      const temp = questions[qIndex]
      this.$set(questions, qIndex, questions[targetIndex])
      this.$set(questions, targetIndex, temp)
    },
    // 提交试卷数据结构
    // 统一重置页面所有数据状态
    resetPaperPage() {
      // 1. 重置基础表单字段和大题数组
      this.paperForm = {
        title: '',
        subjectId: '',
        sections: []
      }

      // 2. 重置弹窗查询、分页、列表及多选缓存
      this.queryParams = {
        pageNumber: 1,
        subjectId: '',
        type: '1'
      }
      this.currentPage = 1
      this.pageSize = 10
      this.totalSize = 0
      this.dataList = []
      this.selectedRows = []
      this.searchKey = ''
      this.currentSectionIndex = null

      // 3. 移除 ElementUI 的表单校验残余红字
      this.$nextTick(() => {
        if (this.$refs.paperForm) {
          this.$refs.paperForm.clearValidate()
        }
      })
    },
    // 提交试卷数据结构
    submitPaper() {
      this.$refs.paperForm.validate((valid) => {
        if (!valid) return
        if (this.paperForm.sections.length === 0) {
          return this.$message.error('试卷至少需要包含一个大题分块！')
        }

        // 检查是否有大题未配置任何题目
        const hasEmptySec = this.paperForm.sections.some(s => s.questions.length === 0)
        if (hasEmptySec) {
          return this.$message.error('存在没有添加题目的空大题，请检查或移除！')
        }

        this.paperForm.totalScore = this.totalScore
        addPaper(this.paperForm).then(resp => {
          if (resp.code === 0) {
            this.$message.success('试卷组装完毕，已成功保存至系统！')
            // 【核心改动】成功后执行重置
            this.resetPaperPage()
          } else {
            this.$message.warning(resp.msg)
          }
        }).catch(error => {
          this.$message.error(error.message)
        })
      })
    },
    handleBack() {
      if (this.isChanged) {
        this.$confirm('数据尚未保存，确定要返回吗？', '提示', { type: 'warning' })
          .then(() => this.$router.push('/exam/paper')).catch(() => {})
      } else {
        this.$router.push('/exam/paper')
      }
    }
  }
}
</script>

<style scoped>
.paper-assembly-container {
  background: #fff;
  padding: 10px;
}
.score-summary-zone {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 47px;
}
.total-score-badge {
  background-color: #f0f2f5;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: bold;
  color: #606266;
}
.total-score-badge span {
  color: #f56c6c;
  font-size: 24px;
  margin: 0 4px;
}
.sections-wrapper {
  margin-top: 30px;
}
.section-main-title {
  margin: 0;
  font-size: 16px;
  color: #303133;
}
.section-card {
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  margin-bottom: 25px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.02);
}
.section-card-header {
  background-color: #f5f7fa;
  padding: 12px 15px;
  border-bottom: 1px solid #dcdfe6;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}
.sec-num-text {
  font-weight: bold;
  color: #909399;
}
.sec-score-text {
  font-size: 14px;
  color: #606266;
}
.sec-score-text strong {
  color: #409eff;
  font-size: 16px;
}
.section-card-body {
  padding: 15px;
}
/* 组卷表格核心控制排版 */
.question-assemble-table {
  width: 100%;
  border-collapse: collapse;
}
.question-assemble-table th, .question-assemble-table td {
  border: 1px solid #ebeef5;
  padding: 10px;
  font-size: 14px;
  text-align: left;
}
.question-assemble-table th {
  background-color: #fafafa;
  color: #606266;
}
.table-q-content {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  color: #303133;
}
/* 复合组合题试卷微调盒子 */
.table-sub-children-box {
  margin-top: 10px;
  background-color: #fdf6ec;
  border: 1px dashed #e6a23c;
  padding: 8px 12px;
  border-radius: 4px;
}
.sub-child-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  font-size: 12px;
  color: #e6a23c;
}
.text-center { text-align: center !important; }
.text-right { text-align: right !important; }
.font-bold { font-weight: bold; }
.help-text { color: #909399; font-size: 11px; display: block; margin-top: 2px; text-align: center; }
.paper-footer-bar {
  margin-top: 40px;
  padding: 20px 0;
  border-top: 1px solid #ebeef5;
  text-align: center;
}

.optimized-paper-form {
  background: #fafbfe;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

/* 调整右侧总分区域的 Flex 布局，使其完美贴合右侧顶端 */
.score-summary-zone {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  /* 移除原有的固定 height: 47px，让它自适应弹性盒 */
}

/* 重新设计更具现代感的总分 Badge */
.total-score-badge {
  background: linear-gradient(135deg, #ffeded 0%, #ffe3e3 100%);
  border: 1px solid #ffcbcb;
  padding: 8px 18px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  box-shadow: 0 2px 6px rgba(245, 108, 108, 0.1);
}

.total-score-badge .badge-label {
  font-size: 12px;
  color: #f56c6c;
  margin-bottom: 2px;
  letter-spacing: 1px;
}

.total-score-badge .badge-value {
  font-size: 13px;
  color: #f56c6c;
  font-weight: bold;
}

.total-score-badge .badge-value span {
  font-size: 28px;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  margin-right: 2px;
  line-height: 1;
}
</style>
